import { useEffect, useRef } from 'react';

const CHANNEL_NAME = 'chat-tab-detection';
const HEARTBEAT_INTERVAL = 1000; // 1 segundo
const TAB_TIMEOUT = 3000; // 3 segundos sem heartbeat = aba fechada

/**
 * Hook para detectar quando todas as abas são fechadas e limpar o chat
 */
export const useTabCloseDetection = () => {
  const channelRef = useRef<BroadcastChannel | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastHeartbeatRef = useRef<Map<string, number>>(new Map());
  const tabIdRef = useRef<string>(`tab-${Date.now()}-${Math.random()}`);

  useEffect(() => {
    // Só funciona em navegadores modernos
    if (typeof BroadcastChannel === 'undefined') {
      return undefined;
    }

    channelRef.current = new BroadcastChannel(CHANNEL_NAME);

    // Enviar heartbeat periódico
    const sendHeartbeat = () => {
      if (channelRef.current) {
        channelRef.current.postMessage({
          type: 'heartbeat',
          tabId: tabIdRef.current,
          timestamp: Date.now(),
        });
      }
    };

    // Enviar heartbeat imediatamente e depois periodicamente
    sendHeartbeat();
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);

    // Listener para mensagens de outras abas
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'heartbeat' && event.data.tabId !== tabIdRef.current) {
        lastHeartbeatRef.current.set(event.data.tabId, event.data.timestamp);
      }
    };

    channelRef.current.addEventListener('message', handleMessage);

    // Verificar se somos a última aba
    const checkLastTab = () => {
      const now = Date.now();
      const activeTabs: string[] = [];

      lastHeartbeatRef.current.forEach((timestamp, tabId) => {
        if (now - timestamp < TAB_TIMEOUT) {
          activeTabs.push(tabId);
        } else {
          lastHeartbeatRef.current.delete(tabId);
        }
      });

      // Se não há outras abas ativas (além de nós mesmos), limpar chat
      // Aguardar um pouco para garantir que não há outras abas iniciando
      // DESABILITADO: Mantém historico do chat ao fechar abas
      // if (activeTabs.length === 0) {
      //   setTimeout(() => {
      //     // Verificar novamente antes de limpar
      //     const hasActiveTabs = Array.from(lastHeartbeatRef.current.values()).some(
      //       timestamp => Date.now() - timestamp < TAB_TIMEOUT,
      //     );

      //     if (!hasActiveTabs) {
      //       localStorage.removeItem(STORAGE_KEYS.CHAT_MESSAGES);
      //       localStorage.removeItem(STORAGE_KEYS.CHAT_SETTINGS);
      //     }
      //   }, TAB_TIMEOUT);
      // }
    };

    const checkInterval = setInterval(checkLastTab, HEARTBEAT_INTERVAL);

    // Listener para beforeunload
    const handleBeforeUnload = () => {
      // Remover nosso heartbeat do mapa
      lastHeartbeatRef.current.delete(tabIdRef.current);

      // Enviar mensagem de despedida
      if (channelRef.current) {
        channelRef.current.postMessage({
          type: 'tab-closing',
          tabId: tabIdRef.current,
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (checkInterval) {
        clearInterval(checkInterval);
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (channelRef.current) {
        channelRef.current.removeEventListener('message', handleMessage);
        channelRef.current.close();
      }
    };
  }, []);
};
